import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, BookOpen, Building2, FileStack, Loader2, RefreshCcw, Scale, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { compactText, formatLegalDate, getSeverityTone, getStatusTone } from '@/lib/legalOs';
import { loadAuthoritiesOverview } from '@/lib/workflowData';
import { AppEmptyState, AppMetricCard, AppPage, AppPageHeader, AppSectionHeader } from '@/components/layout/AppPage';
import WorkflowCompatibilityNotice from '@/components/layout/WorkflowCompatibilityNotice';

const TRUST_ICONS = {
  verified_primary: Building2,
  verified_secondary: Scale,
  official_portal: ShieldCheck,
  matter_file: FileStack,
};

function SurfaceCard({ title, children, action }) {
  return (
    <section
      className="advoq-card"
      style={{
        padding: '18px',
        borderRadius: '24px',
        border: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)',
        display: 'grid',
        gap: '14px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '17px' }}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function AuthoritiesPage() {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadNotice, setLoadNotice] = useState('');

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const { data, notice } = await loadAuthoritiesOverview({
        apiBase: API,
        headers: getAuthHeaders(),
        user,
      });
      setOverview(data);
      setLoadNotice(notice);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, user]);

  useEffect(() => {
    document.title = 'Authorities — Advoq';
    loadOverview();
  }, [loadOverview]);

  const metrics = useMemo(() => ([
    {
      icon: Building2,
      label: 'Official families',
      value: overview?.registry_metrics?.source_family_count || 0,
      note: 'Judicial, statute, gazette, and regulator families scaffolded for verified ingestion.',
      accent: '#0f766e',
    },
    {
      icon: ShieldCheck,
      label: 'Adapters ready',
      value: overview?.registry_metrics?.adapter_ready_count || 0,
      note: 'Source families already shaped for crawl-safe ingestion.',
      accent: '#2563eb',
    },
    {
      icon: Scale,
      label: 'Matter authority views',
      value: overview?.matter_authority_views?.length || 0,
      note: 'Live matters with authority stacks attached.',
      accent: '#b45309',
    },
    {
      icon: BookOpen,
      label: 'Material types',
      value: overview?.registry_metrics?.material_types_supported?.length || 0,
      note: 'Supported public-law material types across the verified corpus.',
      accent: '#7c3aed',
    },
  ]), [overview]);

  return (
    <main className="app-main" data-testid="authorities-page">
      <AppPage>
        <AppPageHeader
          eyebrow="Authorities"
          title={overview?.headline || 'Authorities and verified public law layer'}
          description="Verified primary law first, verified secondary support second, portal-only records marked honestly, and matter files clearly separated from inference."
          actions={(
            <button type="button" className="app-chat-button" onClick={loadOverview} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
              Refresh
            </button>
          )}
          meta={(
            <div className="app-chip-row">
              {overview?.role?.label ? <span className="app-page-chip">{overview.role.label}</span> : null}
              <span className="app-page-chip">Verification is explicit</span>
              <span className="app-page-chip">Official-source-first retrieval</span>
            </div>
          )}
        />
        <WorkflowCompatibilityNotice message={loadNotice} />

        <div className="app-responsive-metrics-grid">
          {metrics.map((item) => (
            <AppMetricCard key={item.label} {...item} />
          ))}
        </div>

        <div className="app-split-grid app-split-grid--balanced">
          <SurfaceCard title="Trust guide">
            {(overview?.trust_guide || []).map((item) => {
              const Icon = TRUST_ICONS[item.id] || AlertTriangle;
              const tone = item.id === 'verified_primary'
                ? getStatusTone('ready')
                : item.id === 'verified_secondary'
                  ? getSeverityTone('low')
                  : item.id === 'official_portal'
                    ? getSeverityTone('medium')
                    : getStatusTone('watch');
              return (
                <div
                  key={item.id}
                  className="advoq-card"
                  style={{
                    padding: '14px',
                    borderRadius: '18px',
                    border: '1px solid var(--border-subtle)',
                    background: 'var(--bg-elevated)',
                    display: 'grid',
                    gap: '8px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <span style={{ color: tone.color }}><Icon size={14} /></span>
                      <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>{item.label}</div>
                    </div>
                    <span className="app-page-chip" style={{ background: tone.background, color: tone.color, border: `1px solid ${tone.border}` }}>
                      {item.short_label}
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
                    {item.description}
                  </div>
                </div>
              );
            })}
          </SurfaceCard>

          <SurfaceCard title="Verification policy">
            {overview?.product_policy ? (
              <div style={{ display: 'grid', gap: '12px' }}>
                {Object.entries(overview.product_policy).map(([key, value]) => (
                  <div
                    key={key}
                    className="advoq-card"
                    style={{
                      padding: '14px',
                      borderRadius: '18px',
                      border: '1px solid var(--border-subtle)',
                      background: 'var(--bg-elevated)',
                    }}
                  >
                    <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700, textTransform: 'capitalize' }}>
                      {key.replace(/_/g, ' ')}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6, marginTop: '6px' }}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </SurfaceCard>
        </div>

        <section style={{ display: 'grid', gap: '16px' }}>
          <AppSectionHeader
            eyebrow="Live matters"
            title="Authority stacks"
            description="Matter-by-matter authority cards with treatment, bench strength, precedential weight, and verification band visible at a glance."
          />
          {!loading && !(overview?.matter_authority_views || []).length ? (
            <AppEmptyState
              icon={Scale}
              title="No matter authority stacks yet"
              description="Refresh authorities from a matter board and the structured authority cards will appear here."
            />
          ) : null}
          {(overview?.matter_authority_views || []).length ? (
            <div style={{ display: 'grid', gap: '16px' }}>
              {overview.matter_authority_views.map((view) => (
                <SurfaceCard
                  key={view.matter_id}
                  title={view.title}
                  action={<button type="button" className="app-chat-button" onClick={() => navigate(`/war-room/${view.matter_id}`)}>Open matter <ArrowRight size={14} /></button>}
                >
                  <div className="app-chip-row">
                    <span className="app-page-chip">{view.court_name || 'Court to confirm'}</span>
                    {view.next_hearing_date ? <span className="app-page-chip">Hearing {formatLegalDate(view.next_hearing_date)}</span> : null}
                    <span className="app-page-chip">{view.summary?.count || 0} authority cards</span>
                  </div>
                  {(view.summary?.cards || []).length ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
                      {view.summary.cards.map((card) => {
                        const treatmentTone = getSeverityTone(card.treatment?.severity || 'low');
                        const verificationTone = card.verification?.id === 'verified_primary'
                          ? getStatusTone('ready')
                          : card.verification?.id === 'verified_secondary'
                            ? getSeverityTone('low')
                            : getSeverityTone('medium');
                        return (
                          <a
                            key={card.authority_id || card.title}
                            href={card.url || '#'}
                            target="_blank"
                            rel="noreferrer"
                            className="advoq-card"
                            style={{
                              padding: '16px',
                              borderRadius: '20px',
                              border: '1px solid var(--border-subtle)',
                              background: 'var(--bg-elevated)',
                              display: 'grid',
                              gap: '10px',
                              textDecoration: 'none',
                            }}
                          >
                            <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700, lineHeight: 1.45 }}>
                              {card.title}
                            </div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
                              {compactText(card.headline || card.relevance_note || 'Authority card ready for review.', 180)}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <span className="app-page-chip" style={{ background: verificationTone.background, color: verificationTone.color, border: `1px solid ${verificationTone.border}` }}>
                                {card.verification?.label}
                              </span>
                              <span className="app-page-chip" style={{ background: treatmentTone.background, color: treatmentTone.color, border: `1px solid ${treatmentTone.border}` }}>
                                {card.treatment?.label}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: '12px' }}>
                              <span>{card.docsource || 'Source pending'}</span>
                              <span>{card.bench_strength?.label || 'Bench not surfaced'}</span>
                              <span>{card.precedential_weight?.label || 'Weight pending'}</span>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                      This matter does not have attached authority cards yet.
                    </div>
                  )}
                </SurfaceCard>
              ))}
            </div>
          ) : null}
        </section>

        <section style={{ display: 'grid', gap: '16px' }}>
          <AppSectionHeader
            eyebrow="Verified corpus"
            title="Registry highlights"
            description="Priority official families already scaffolded for Supreme Court, High Courts, eCourts, India Code, eGazette, and regulators."
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
            {(overview?.registry_highlights || []).map((item) => (
              <div
                key={item.source_family_id}
                className="advoq-card"
                style={{
                  padding: '16px',
                  borderRadius: '20px',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-surface)',
                  display: 'grid',
                  gap: '8px',
                }}
              >
                <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700 }}>{item.label}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
                  {compactText(item.normalization_rules?.[0] || item.label, 180)}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span className="app-page-chip">{item.trust_class}</span>
                  <span className="app-page-chip">{item.reasoning_eligibility}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </AppPage>
    </main>
  );
}
